import { memone } from '@deqo/foundation';
import { Client, ClientManager, Component, ObjectManager } from '../../../../concepts/objects';
import { ActiveObject, resolve } from '../../../Container';
import { PreferencesModel } from '../../../preferences/PreferencesModel';
import { Physical, RelativeTo } from '../../entities/Physical';
import { Participant } from '../../participants/Participant';
import { ParticipantManager } from '../../participants/ParticipantManager';
import { Pawn } from './Pawn';
import { PawnManager } from './PawnManager';

export class PawnMechanics extends ActiveObject {

    @resolve(ClientManager) private clients: ClientManager;
    @resolve(ObjectManager) private objects: ObjectManager;
    @resolve(ParticipantManager) private participants: ParticipantManager;
    @resolve(PawnManager) private pawns: PawnManager;
    @resolve(PreferencesModel) private preferences!: PreferencesModel;

    private get adapterComponentIndex() {
        return this.buildPawnAdapterComponentIndex(this.pawns.getState());
    }

    public start() {
        for (const client of this.clients.others) {
            this.allocatePawn(client);
            this.updatePawn(client);
        }
        this.burden(
            this.clients.on('join', this.onClientJoin),
            this.clients.on('leave', this.onClientLeave),
            this.clients.on('update', this.onClientUpdate),
            this.participants.on('update', this.onParticipantUpdate),
            this.objects.on('component', 'affected', this.onComponentUpdate),
            // () => this.visuals.clear(),
        );
    }

    private onClientJoin = (client: Client) => {
        if (client.self) return;
        this.allocatePawn(client);
        this.updatePawn(client);
    };

    private onClientLeave = (client: Client) => {
        if (client.self) return;
        this.releasePawn(client);
    };

    private onClientUpdate = ([client]: [Client, string[]]) => {
        if (client.self) return;
        this.updatePawn(client, true);
    };

    // private onClientOffset = ([id, offset]: [string, Point]) => {
    //     const client = this.clients.get(id);
    //     if (client.self) return;
    //     const visual = this.visuals.get(client.id);
    //     if (!visual) return;
    //     visual.update(s => {
    //         s.x = s.x + offset.x;
    //         s.y = s.y + offset.y;
    //     });
    // };

    private onParticipantUpdate = (ptpt: Participant) => {
        if (ptpt.self) return;
        for (const client of this.clients.getForUser(ptpt.id)) {
            this.updatePawn(client, false);
        }
    };

    private onComponentUpdate = ([cmpt]: [Component]) => {
        const clientId = this.adapterComponentIndex.get(cmpt.id);
        if (!clientId) return;
        const client = this.clients.get(clientId);
        if (!client) return;
        this.updatePawn(client);
    };

    private allocatePawn(client: Client) {
        this.pawns.create(client.id);
    }

    private releasePawn(client: Client) {
        this.pawns.delete(client.id);
    }

    private updatePawn(client: Client, forceAdapterUpdate?: boolean) {
        const pawn = this.pawns.get(client.id);
        if (!pawn) return;

        const ptpt = this.participants.get(client.userId);

        // const callerId = (client.get('callerId') ?? null) as string;
        // const feed = (call.members ?? {})[callerId];
        // const face = client.get('face') as string ?? null;

        const x = (client.get('px') as number);
        const y = (client.get('py') as number);

        const inactive = this.preferences.hideOthers === true && (
            /.*:idle|.*:panning|.*:panningPrimed/.test(client.get('pt') ?? ':idle') &&
            (client.get('pa') ?? 'none:none') === 'none:none'
        );
        const visible = !!client.get('track') &&
            !isNaN(x + y) &&
            ptpt?.presence === 'active' &&
            !inactive;

        const opacity = client.get('pv') ? 1 : 0;

        const [id] = (client.get('pa')?.toString() || 'none:none').split(':');
        pawn.mutate(s => {
            s.x = x ?? 0;
            s.y = y ?? 0;
            // s.feed = feed;
            // s.face = face;
            s.hat = ptpt?.hat;
            if (s.visible !== visible) {
                s.visible = visible;
                s.label = Participant.toInitials(ptpt?.name ?? '');
            }
            if (s.opacity !== opacity) {
                s.opacity = opacity;
            }
            if (s.imageUrl !== ptpt?.photo) {
                s.imageUrl = ptpt?.photo;
            }
            if (s.color !== ptpt?.color) {
                s.color = ptpt?.color;
            }
            if (forceAdapterUpdate || s.adapter[0] !== id) {
                s.adapter = this.computeVisualAdapterPath(id);
            }
        });
    }

    private computeVisualAdapterPath(target: string) {
        if (!target || target === 'none') return [target] as const;
        if (!this.objects.has(target, Physical)) return [target] as const;
        const elmt = this.objects.get(target, Physical);
        const outline = elmt.path(RelativeTo.Model);
        return [target, outline] as const;
    }

    @memone()
    private buildPawnAdapterComponentIndex(pawns: Pawn[]) {
        return new Map(pawns.map(x => [x.id, (x.adapter ?? [])[0]]));
    }

    // private updateVisualAdapterIndex(client: Client) {
    //     const visual = this.visuals.get(client.id);
    //     const current = this.visualAdapterTargetIndex.get(client.id) ?? null;

    //     const [id] = visual?.adapter ?? [];
    //     if (id === current) return;

    //     if (current && current !== id) {
    //         this.visualAdapterTargetIndex.delete(current);
    //         this.visualAdapterTargetIndex.delete(client.id);
    //     }
    //     if (visual && !!id) {
    //         this.visualAdapterTargetIndex.set(id, client.id);
    //         this.visualAdapterTargetIndex.set(client.id, id);
    //     }
    // }
}
